import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Star,
  MessageCircle,
  Phone,
  Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`/bookings/${id}`);
      setBooking(response.data.booking);
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      toast.error('Failed to load booking details');
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    setCancelLoading(true);
    try {
      await axios.put(`/bookings/${id}/cancel`, {
        reason: 'Cancelled by guest'
      });
      toast.success('Booking cancelled successfully');
      fetchBooking(); // Refresh booking data
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to cancel booking';
      toast.error(message);
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'pending':
        return <Clock className="text-yellow-500" size={24} />;
      case 'cancelled':
        return <XCircle className="text-red-500" size={24} />;
      case 'completed':
        return <CheckCircle className="text-blue-500" size={24} />;
      default:
        return <Clock className="text-gray-500" size={24} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isGuest = booking && booking.guest._id === user._id;
  const canCancel = booking && booking.status === 'confirmed' && new Date(booking.checkIn) > new Date();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Not Found</h2>
          <Link
            to="/bookings"
            className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Bookings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/bookings')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Bookings</span>
            </button>
            
            <div className="flex items-center space-x-3">
              {getStatusIcon(booking.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
                {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Property Info */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2">
                  <img
                    src={booking.listing.images?.[0]?.url || '/images/placeholder-property.jpg'}
                    alt={booking.listing.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-6">
                  <Link
                    to={`/listings/${booking.listing._id}`}
                    className="text-2xl font-bold text-gray-900 hover:text-primary-600 transition-colors mb-2 block"
                  >
                    {booking.listing.title}
                  </Link>
                  <div className="flex items-center text-gray-600 mb-4">
                    <MapPin size={16} className="mr-1" />
                    <span>{booking.listing.location.city}, {booking.listing.location.state}</span>
                  </div>
                  <div className="text-3xl font-bold text-primary-600">
                    ${booking.listing.price}
                    <span className="text-sm text-gray-500 font-normal">/ night</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Booking Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Calendar className="text-primary-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Check-in</p>
                      <p className="font-semibold">
                        {format(new Date(booking.checkIn), 'EEEE, MMMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Calendar className="text-primary-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Check-out</p>
                      <p className="font-semibold">
                        {format(new Date(booking.checkOut), 'EEEE, MMMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Users className="text-primary-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Guests</p>
                      <p className="font-semibold">
                        {booking.guests.adults} adults
                        {booking.guests.children > 0 && `, ${booking.guests.children} children`}
                        {booking.guests.infants > 0 && `, ${booking.guests.infants} infants`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Clock className="text-primary-600" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Duration</p>
                      <p className="font-semibold">{booking.nights} nights</p>
                    </div>
                  </div>
                </div>
              </div>

              {booking.specialRequests && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Special Requests</h4>
                  <p className="text-gray-700">{booking.specialRequests}</p>
                </div>
              )}
            </div>

            {/* Contact Information */}
            {!isGuest && (
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Guest Information</h3>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                    <Users size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {booking.guest.firstName} {booking.guest.lastName}
                    </p>
                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Mail size={14} />
                        <span className="text-sm">{booking.guest.email}</span>
                      </div>
                      {booking.guest.phone && (
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Phone size={14} />
                          <span className="text-sm">{booking.guest.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Info */}
            {booking.status === 'cancelled' && booking.cancellationReason && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Cancellation Details</h3>
                <p className="text-red-700">{booking.cancellationReason}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Summary */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Price Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>${booking.listing.price} x {booking.nights} nights</span>
                  <span>${booking.listing.price * booking.nights}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span>${booking.totalAmount}</span>
                  </div>
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  Payment Status: <span className="capitalize">{booking.paymentStatus}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-soft p-6 space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <Link
                to={`/listings/${booking.listing._id}`}
                className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors text-center block"
              >
                View Property
              </Link>

              {isGuest && canCancel && (
                <button
                  onClick={cancelBooking}
                  disabled={cancelLoading}
                  className="w-full bg-red-100 text-red-700 py-3 px-4 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
                </button>
              )}

              {booking.status === 'completed' && isGuest && !booking.review?.rating && (
                <button className="w-full bg-yellow-100 text-yellow-700 py-3 px-4 rounded-lg hover:bg-yellow-200 transition-colors">
                  Write Review
                </button>
              )}

              <button className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2">
                <MessageCircle size={18} />
                <span>Contact {isGuest ? 'Host' : 'Guest'}</span>
              </button>
            </div>

            {/* Booking ID */}
            <div className="bg-gray-100 rounded-xl p-4">
              <p className="text-sm text-gray-600">Booking ID</p>
              <p className="font-mono text-sm text-gray-900">{booking._id}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
